'use client';

import { useTranslations } from 'next-intl';

import EyeSmallIcon from '@/assets/icons/eye-small.svg';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { P } from '@/components/shared/Typography';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils/cn';
import { isPriceEqual } from '@/lib/utils/isPriceEqual';
import { T_MySubmission } from '@/ts/types/T_MySubmissions';
import { T_PredictionWinnerModalType } from '@/ts/types/T_PredictionWinnerModalType';

interface I_SubmissionsListProps {
  submissions: T_MySubmission[];
  total: number;
  isLoading: boolean;
  type?: T_PredictionWinnerModalType;
  winningPrices?: number[];
  decimals?: number;
  onLoadMore: () => void;
  onSelectSubmission: (submission: T_MySubmission) => void;
}

export const SubmissionsList = ({
  submissions,
  total,
  isLoading,
  type,
  winningPrices,
  decimals = 5,
  onLoadMore,
  onSelectSubmission
}: I_SubmissionsListProps) => {
  const t = useTranslations();
  if (isLoading && submissions.length === 0) {
    return (
      <div className="flex justify-center items-center py-8">
        <LoadingSpinner size={24} />
      </div>
    );
  }

  if (submissions.length === 0) {
    const emptyText =
      type === 'missed' || type === 'ongoing' || type === 'resolving'
        ? t('profile.predictionWinners.noSubmissionsFound') ||
          'No submissions found'
        : t('profile.predictionWinners.noWinnersFound');

    return (
      <div className="flex justify-center items-center py-8">
        <P className="text-muted-foreground">{emptyText}</P>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-10 outline-1 outline-base-border/20 overflow-hidden flex flex-col max-h-100">
        <div className="px-2 py-2.5 border-b border-base-border/20 flex items-center justify-center">
          <P className="text-muted-foreground text-sm font-bold leading-5">
            {t('profile.predictionWinners.submissions')}
          </P>
        </div>
        <div className="flex h-40 overflow-y-auto flex-1">
          <div className="flex-1 flex flex-col w-full">
            {submissions
              .map(submission => {
                // Calculate count excluding winning prices
                // Use winningPrices from response level (prop) instead of submission level
                let predictionCount =
                  submission.predictedPricesDecimal?.length || 0;
                let filteredPrices: string[] = [];

                if (
                  winningPrices &&
                  winningPrices.length > 0 &&
                  submission.predictedPricesDecimal
                ) {
                  filteredPrices = submission.predictedPricesDecimal.filter(
                    priceStr => {
                      // Check if this price is NOT in the winningPrices array
                      // Use decimal-aware comparison (2060.19 !== 2060.20)
                      const compareDecimals = Math.max(decimals, 5);
                      return !winningPrices.some(winningPrice =>
                        isPriceEqual(priceStr, winningPrice, compareDecimals)
                      );
                    }
                  );
                  predictionCount = filteredPrices.length;
                } else if (submission.predictedPricesDecimal) {
                  filteredPrices = submission.predictedPricesDecimal;
                }

                return { submission, predictionCount, filteredPrices };
              })
              .filter(({ filteredPrices }) => filteredPrices.length >= 1)
              .map(({ submission, predictionCount }, index, filteredArray) => {
                const predictionText =
                  predictionCount === 1
                    ? t('profile.transferHistory.prediction')
                    : t('profile.transferHistory.predictions');

                return (
                  <div
                    key={submission.id}
                    className={cn(
                      'min-w-20 py-2.5 px-6 flex justify-between items-center',
                      index !== filteredArray.length - 1 &&
                        'border-b border-base-border/20'
                    )}
                  >
                    <P className="text-foreground text-sm font-medium leading-5">
                      {predictionCount} {predictionText}
                    </P>
                    <Button
                      variant="outline"
                      size="icon"
                      className="w-4 h-4"
                      icon={<EyeSmallIcon className="text-foreground" />}
                      onClick={() => onSelectSubmission(submission)}
                    />
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      {submissions.length < total && total > 20 && !isLoading && (
        <div className="pt-6">
          <Button
            variant="outline"
            size="lg"
            className="w-full uppercase"
            onClick={onLoadMore}
            disabled={isLoading}
          >
            {t('profile.predictionWinners.loadMore')}
          </Button>
        </div>
      )}

      {isLoading && submissions.length > 0 && (
        <div className="flex justify-center items-center py-4">
          <LoadingSpinner size={24} />
        </div>
      )}
    </div>
  );
};
